import { env } from '@config/env';
import { brand } from '@shared/lib/branded';
import { PERMISSIONS } from './permissions';
import type { User } from './types';

/**
 * Auth against the Verofy Connect Node backend (AdonisJS, `verofy-connect-api`).
 *
 * Contract (from connect `app/controllers/auth/user_logins_controller.ts`):
 *   POST /api/auth/login   body: { email, password, token? }
 *     200 → { success:true, id, type:'bearer', token, is_mfa_enabled, role }
 *     402 → { success:false, message:'MFA_TOKEN' }      (MFA required, resend with `token`)
 *     402 → { success:false, message:'Invalide credential' }
 *   GET  /api/users/auth/me (Bearer) → current user profile (best-effort enrichment)
 *
 * MFA is a TOTP code passed as `token` in the SAME login call (not a separate
 * SMS step); connect skips MFA entirely when its NODE_ENV=local. Reached via the
 * Vite dev proxy (VITE_CONNECT_AUTH_BASE → connect :3333).
 */

const BASE = env.VITE_CONNECT_AUTH_BASE.replace(/\/+$/, '');

export interface LoginResult {
  readonly accessToken: string;
  readonly user: User;
}

/** Discriminated outcome so the provider can branch credentials → MFA → session. */
export type LoginOutcome =
  | { kind: 'ok'; result: LoginResult }
  | { kind: 'mfa-required' }
  | { kind: 'mfa-setup'; accessToken: string }
  | { kind: 'error'; message: string };

interface LoginEnvelope {
  readonly success?: boolean;
  readonly message?: string;
  readonly id?: number | string;
  readonly token?: string;
  readonly role?: string;
  // Backend sends 0/1 (number); older shapes used a boolean. 0/false ⇒ not yet enrolled.
  readonly is_mfa_enabled?: number | boolean;
}

function jsonHeaders(token?: string): Record<string, string> {
  const h: Record<string, string> = { 'content-type': 'application/json', accept: 'application/json' };
  if (token) h.authorization = `Bearer ${token}`;
  return h;
}

/**
 * Log in with email + password (+ optional MFA TOTP `token`). Returns a
 * discriminated outcome rather than throwing, so the caller stays linear.
 */
export async function login(email: string, password: string, mfaToken?: string): Promise<LoginOutcome> {
  const body: Record<string, unknown> = { email, password };
  if (mfaToken) body.token = mfaToken;

  let res: Response;
  try {
    res = await fetch(`${BASE}/auth/login`, { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(body) });
  } catch {
    return { kind: 'error', message: 'Could not reach the authentication service.' };
  }

  let json: LoginEnvelope = {};
  try { json = (await res.json()) as LoginEnvelope; } catch { /* non-JSON body */ }

  if (res.ok && json.token) {
    // Fresh account whose MFA is not configured yet: connect stores the token but
    // forces TOTP enrolment (QR → verify) before the session is usable. Mirror that.
    if (json.is_mfa_enabled === 0 || json.is_mfa_enabled === false) {
      return { kind: 'mfa-setup', accessToken: json.token };
    }
    const user = await fetchMe(json.token, email, json);
    return { kind: 'ok', result: { accessToken: json.token, user } };
  }

  // connect throws a 402 with message 'MFA_TOKEN' when a code is needed.
  if (/mfa/i.test(json.message ?? '')) {
    return { kind: 'mfa-required' };
  }

  return { kind: 'error', message: json.message ?? 'Those credentials were not accepted.' };
}

/** Best-effort profile lookup; falls back to the login data if /me is unavailable. */
async function fetchMe(token: string, email: string, login: LoginEnvelope): Promise<User> {
  try {
    const res = await fetch(`${BASE}/users/auth/me`, { headers: jsonHeaders(token) });
    if (res.ok) {
      const me = (await res.json()) as Record<string, unknown>;
      // connect may wrap in { success, ...data } or return the user directly.
      const u = ((me.user as Record<string, unknown> | undefined) ?? me) ?? {};
      return mapUser(u, email, login);
    }
  } catch { /* fall through to login-data mapping */ }
  return mapUser({}, email, login);
}

/**
 * Request a password reset — connect `POST /api/auth/password/forgot_password` { email }.
 * Mirrors connect's `handleForgotPassword` (AuthContext): 200 → "sent" message; otherwise
 * the backend message. Note: connect's `userCanUpdate` middleware 403s if the email is not
 * a known user, so this only succeeds for accounts that exist in the target backend's DB.
 */
export async function forgotPassword(email: string): Promise<{ ok: boolean; message: string }> {
  try {
    const res = await fetch(`${BASE}/auth/password/forgot_password`, {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({ email }),
    });
    let json: { success?: boolean; message?: string } = {};
    try { json = (await res.json()) as { success?: boolean; message?: string }; } catch { /* non-JSON */ }
    if (res.ok && json.success !== false) {
      return { ok: true, message: 'Password reset link sent to your email.' };
    }
    return { ok: false, message: json.message ?? 'Something went wrong. Try again.' };
  } catch {
    return { ok: false, message: 'Failed to send reset link. Please check your email.' };
  }
}

/**
 * MFA enrolment step 1 — fetch the TOTP QR (connect `GET /api/users/auth/mfa/generate`,
 * Bearer). Mirrors `SetupMFAComponent.fetchMFAImage`: 200 → { qrCode } (a data-URI the
 * `<img>` renders). Returns null on any failure so the caller can toast and stay put.
 */
export async function generateMfa(token: string): Promise<string | null> {
  try {
    const res = await fetch(`${BASE}/users/auth/mfa/generate`, { headers: jsonHeaders(token) });
    if (!res.ok) return null;
    const json = (await res.json()) as { qrCode?: string };
    return json.qrCode ?? null;
  } catch {
    return null;
  }
}

/**
 * MFA enrolment step 2 — confirm the 6-digit code (connect `POST /api/users/auth/mfa/verify`,
 * Bearer, body `{ token }`). Mirrors `SetupMFAComponent.handleVerifyMfa`: 200 → enrolled.
 */
export async function verifyMfaSetup(token: string, code: string): Promise<{ ok: boolean; message: string }> {
  try {
    const res = await fetch(`${BASE}/users/auth/mfa/verify`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify({ token: code }),
    });
    let json: { success?: boolean; message?: string } = {};
    try { json = (await res.json()) as { success?: boolean; message?: string }; } catch { /* non-JSON */ }
    if (res.ok && json.success !== false) return { ok: true, message: '' };
    return { ok: false, message: json.message ?? 'That code was not accepted. Please try again.' };
  } catch {
    return { ok: false, message: 'Verification failed. Please try again.' };
  }
}

/** Best-effort logout — connect invalidates the access token server-side if supported. */
export async function logoutRequest(token: string | null): Promise<void> {
  if (!token) return;
  try { await fetch(`${BASE}/auth/logout`, { method: 'POST', headers: jsonHeaders(token) }); } catch { /* local logout wins */ }
}

/**
 * Map connect's user onto the console's `User`, mirroring the Connect role model:
 *   admin (Verofy user)      -> full console, sees every customer
 *   isvAdmin                 -> everything except Companies; sees only its own
 *                               customers (the API scopes the list to its company)
 *   developer / installer    -> no Customers (the API refuses them)
 * The console gates presentation; the API authorises every call via the Bearer.
 */
function mapUser(u: Record<string, unknown>, email: string, login: LoginEnvelope): User {
  // Real backend role from /users/auth/me (preferred) or the login envelope.
  const beRole = String((u.role as string | undefined) ?? login.role ?? 'admin');
  const roleMap: Record<string, User['role']> = {
    admin: 'admin',
    isvAdmin: 'operator',
    developer: 'developer',
    installer: 'viewer',
  };
  // Permissions withheld per Connect role, derived from the Connect API's own
  // route gates (the source of truth):
  //   customers        -> admin (all) + isvAdmin (own company only, scoped server-side)
  //   companies list   -> onlyAdminCanAccess
  //   estate (devices + softpos) -> adminAndISVCanAccess
  // Users / API keys / Products are open to every role — the API scopes their
  // data to the caller's own company server-side.
  const WITHHELD: Record<string, readonly string[]> = {
    admin: [],
    isvAdmin: ['connect.companies.view'],
    developer: ['customers.view', 'connect.companies.view', 'connect.estate.view'],
    installer: ['customers.view', 'connect.companies.view', 'connect.estate.view'],
  };
  // Unknown roles fail safe to the most restricted set.
  const withheld = WITHHELD[beRole] ?? WITHHELD['installer'] ?? [];
  // connect `/users/auth/me` returns { userId, fullName, email, role, companyName,
  // companySuffix, posProviderId, ... }. Older/native shapes used first_name/name/company.
  const composed = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  const beFullName = (u.fullName as string | undefined) ?? '';
  const nameRaw = (u.name as string | undefined) ?? (beFullName !== '' ? beFullName : composed);
  const name = nameRaw !== '' ? nameRaw : email;
  const company = (u.company as Record<string, unknown> | undefined) ?? {};
  const org =
    (company.name as string | undefined) ??
    (u.companyName as string | undefined) ??
    (u.org as string | undefined) ??
    'Verofy';
  const idSource = (u.id ?? u.userId ?? login.id) as number | string | undefined;
  const id = idSource != null ? String(idSource) : `usr_${email}`;
  // The caller's provider uuid (their company's pos_provider_id) — used to scope
  // provider-filterable surfaces (Products) for ISV users.
  const posProviderId = (u.posProviderId as string | undefined) ?? undefined;
  // The company this user belongs to. Connect scopes every non-admin write to
  // it server-side, so surfaces that act on "your company" read it from here
  // rather than making the user name a company they have no choice about.
  const companySuffix =
    (u.companySuffix as string | undefined) ?? (company.suffix as string | undefined) ?? undefined;
  const companyName =
    (u.companyName as string | undefined) ?? (company.name as string | undefined) ?? undefined;

  return {
    id: brand(id),
    name,
    email: (u.email as string | undefined) ?? email,
    initials: initialsOf(name),
    org,
    role: roleMap[beRole] ?? 'viewer',
    permissions: PERMISSIONS.filter((p) => !withheld.includes(p)),
    ...(posProviderId ? { posProviderId } : {}),
    ...(companySuffix ? { companySuffix } : {}),
    ...(companyName ? { companyName } : {}),
  };
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}
